import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  bumpAll,
  bumpSignals,
  initialLiveSyncState,
  LIVE_SYNC_SLICE_NAME,
} from './signals';

import type { LiveResource } from './signals';

/**
 * Shared live-sync state (connection + per-aggregate refetch signals): the WS
 * provider writes, the api hooks read. Ephemeral, blacklisted from persist;
 * freshness re-established by `allSignalsBumped` on connect.
 */
export const liveSyncSlice = createSlice({
  name: LIVE_SYNC_SLICE_NAME,
  initialState: initialLiveSyncState,
  reducers: {
    connectionChanged(state, { payload }: PayloadAction<boolean>) {
      state.connected = payload;
      if (payload) {
        state.hasConnected = true;
      }
    },
    // Legacy UpdateMessage / heartbeat path: bump only the known aggregates named.
    signalsBumped(state, { payload }: PayloadAction<string[]>) {
      state.signals = bumpSignals(state.signals, payload);
    },
    // Cold resync on (re)connect: bump every aggregate.
    allSignalsBumped(state) {
      state.signals = bumpAll(state.signals);
    },
    // Live-sync patched a resource's cache data in place. `atMs` is supplied by
    // the caller, not read here: a reducer calling `Date.now()` is impure and
    // cannot be tested against a fixed clock.
    resourceUpdated(
      state,
      { payload }: PayloadAction<{ resource: LiveResource; atMs: number }>,
    ) {
      state.lastLiveUpdateAt[payload.resource] = payload.atMs;
    },
    domainEventHistoryBackfillSettled(state) {
      state.domainEventHistoryBackfillSettled = true;
    },
  },
});

export const {
  connectionChanged,
  signalsBumped,
  allSignalsBumped,
  resourceUpdated,
  domainEventHistoryBackfillSettled,
} = liveSyncSlice.actions;
